import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import {
  FireIcon,
  CalendarDaysIcon,
  ClockIcon,
  TrophyIcon,
} from '@heroicons/react/24/outline';

interface ReadingStatsProps {
  currentStreak: number;
  longestStreak: number;
  totalDaysRead: number;
  totalMinutes: number;
  loading?: boolean;
}

export default function ReadingStats({
  currentStreak,
  longestStreak,
  totalDaysRead,
  totalMinutes,
  loading,
}: ReadingStatsProps) {
  const { isDarkMode } = useTheme();

  const formatTime = (minutes: number): string => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours < 24) {
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    }
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
  };

  const stats = [
    {
      name: 'Current Streak',
      value: `${currentStreak}`,
      unit: currentStreak === 1 ? 'day' : 'days',
      icon: FireIcon,
      color: 'orange',
    },
    {
      name: 'Longest Streak',
      value: `${longestStreak}`,
      unit: longestStreak === 1 ? 'day' : 'days',
      icon: TrophyIcon,
      color: 'yellow',
    },
    {
      name: 'Days Read',
      value: `${totalDaysRead}`,
      unit: '',
      icon: CalendarDaysIcon,
      color: 'cyan',
    },
    {
      name: 'Total Time',
      value: formatTime(totalMinutes),
      unit: '',
      icon: ClockIcon,
      color: 'purple',
    },
  ];

  const getColorClasses = (color: string) => {
    const colorMap: Record<string, { bg: string; text: string }> = {
      orange: {
        bg: isDarkMode ? 'bg-orange-500/10' : 'bg-orange-50',
        text: isDarkMode ? 'text-orange-400' : 'text-orange-600',
      },
      yellow: {
        bg: isDarkMode ? 'bg-yellow-500/10' : 'bg-yellow-50',
        text: isDarkMode ? 'text-yellow-400' : 'text-yellow-600',
      },
      cyan: {
        bg: isDarkMode ? 'bg-cyan-500/10' : 'bg-cyan-50',
        text: isDarkMode ? 'text-cyan-400' : 'text-cyan-600',
      },
      purple: {
        bg: isDarkMode ? 'bg-purple-500/10' : 'bg-purple-50',
        text: isDarkMode ? 'text-purple-400' : 'text-purple-600',
      },
    };
    return colorMap[color];
  };

  if (loading) {
    return (
      <div className="grid grid-cols-2 gap-3">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className={`rounded-xl p-4 ${
              isDarkMode ? 'bg-slate-900/50 border border-slate-700/50' : 'bg-white border border-gray-200'
            }`}
          >
            <div className="animate-pulse">
              <div className={`h-8 w-8 rounded-lg mb-2 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-200'}`} />
              <div className={`h-6 w-12 rounded mb-1 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-200'}`} />
              <div className={`h-3 w-16 rounded ${isDarkMode ? 'bg-slate-700' : 'bg-gray-200'}`} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-3">
      {stats.map((stat) => {
        const colors = getColorClasses(stat.color);
        return (
          <div
            key={stat.name}
            className={`rounded-xl p-4 transition-all ${
              isDarkMode
                ? 'bg-slate-900/50 border border-slate-700/50 hover:border-slate-600'
                : 'bg-white border border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className={`p-2 rounded-lg w-fit ${colors.bg}`}>
              <stat.icon className={`h-5 w-5 ${colors.text}`} />
            </div>
            <div className="mt-2">
              <div className="flex items-baseline gap-1">
                <span className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {stat.value}
                </span>
                {stat.unit && (
                  <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                    {stat.unit}
                  </span>
                )}
              </div>
              <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                {stat.name}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
